import {
  DataDumpAggregateReportsType,
  pnlCategoriesData,
  PnlLosses,
  PnlTagLoss,
  shiftDayWeekTimeRanges,
  TimeRangeFinite,
} from "../types";

import {
  ONE_HOUR_OF_MS,
  ONE_WEEK_OF_MS,
  WEEKS_IN_YEAR,
  NUM_DAYS_IN_WEEK,
  ONE_SECOND_OF_MS,
} from "reports/utils/timeUtils";
import { PnlCategoriesEnum, SimplifiedShiftSegment } from "../types";

import {
  setISOWeek,
  setISODay,
  setHours,
  setMinutes,
  setSeconds,
  addDays,
  addWeeks,
  subWeeks,
  addHours,
  getISOWeek,
} from "date-fns";
import { getWeek } from "date-fns/fp";
import { zonedTimeToUtc } from "date-fns-tz";
import { convertToLocalTime, convertToTimeZone } from "date-fns-timezone";
import { round } from "lodash";
import {
  DATA_DUMP_AGGREGATE_QUERY,
  DATA_DUMP_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import { useQuery } from "@apollo/client";

export const generateEmptyPnlLossesData = () =>
  ({
    asOf: "",
    notScheduled: {
      category: "notScheduled",
      downtimeDependentSpeedLossMs: 0,
      downtimeLossMs: 0,
      downtimePlusDependentSpeedLossMs: 0,
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
      tags: [],
    },
    producing: {
      category: "producing",
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
      minorStops: {
        numberOfEvents: 0,
        averageDurationMs: 0,
      },
      tags: [],
    },
    planned: {
      category: "planned",
      downtimeDependentSpeedLossMs: 0,
      downtimeLossMs: 0,
      downtimePlusDependentSpeedLossMs: 0,
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
      tags: [],
    },

    quality: {
      category: "quality",
      qualityLossMs: 0,
      qualityLossOutput: 0,
      reworkLossMs: 0,
      reworkLossOutput: 0,
      scrapLossMs: 0,
      scrapLossOutput: 0,
    },

    range: {
      ge: "",
      lt: "",
    },

    uncollected: {
      category: "uncollected",
      totalDurationMs: 0,
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
    },
    unknown: {
      category: "unknown",
      downtimeDependentMinorStopLossMs: 0,
      downtimeDependentSlowdownLossMs: 0,
      downtimeDependentSpeedLossMs: 0,
      downtimeLossMs: 0,
      downtimePlusDependentSpeedLossMs: 0,
      independentMinorStopLossMs: 0,
      independentSlowdownLossMs: 0,
      independentSpeedLossMs: 0,
      minorStopLossMs: 0,
      slowdownLossMs: 0,
      speedLossMs: 0,
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
    },

    unplanned: {
      category: "unplanned",
      downtimeDependentSpeedLossMs: 0,
      downtimeLossMs: 0,
      downtimePlusDependentSpeedLossMs: 0,
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
      tags: [],
    },

    waiting: {
      category: "waiting",
      downtimeDependentSpeedLossMs: 0,
      downtimeLossMs: 0,
      downtimePlusDependentSpeedLossMs: 0,
      outputLoss: 0,
      numberOfEvents: 0,
      averageDurationMs: 0,
      tags: [],
    },
  }) as PnlLosses;

export const generateEmptyAggregateData = () => {
  return {
    ge: "",
    lt: "",
    asOf: "",
    pnlLosses: generateEmptyPnlLossesData(),
    machemetryIds: [""],
    processNames: [""],
    calendarTime: 0,
    collectableTime: 0,
    uncollectableTime: 0,
    collectedTime: 0,
    uncollectedTime: 0,
    producingTime: 0,
    productionTime: 0,
    downTime: 0,
    plannedTime: 0,
    unplannedTime: 0,
    waitingTime: 0,
    unknownTime: 0,
    notScheduledTime: 0,
    speedLossTime: 0,
    speedGainTime: 0,
    runningTime: 0,
    minorStopTime: 0,
    slowdownLossTime: 0,
    speedupGainTime: 0,
    rejectLossTime: 0,
    rejectLossTimeNonProducing: 0,
    effectiveRunningTime: 0,
    effectiveProducingTime: 0,
    effectiveProducingTimeTopDown: 0,
    effectiveProducingTimeBottomUp: 0,
    netOutput: 0,
    grossOutput: 0,
    rejectOutput: 0,
    grossOutputNonProducing: 0,
    collectedPotentialOutput: 0,
    productionPotentialOutput: 0,
    producingPotentialOutput: 0,
    totalPotentialOutput: 0,
    qualityPercent: null,
    numberOfStops: 0,
    numberOfMinorStops: 0,
    oeePercent: null,
    oeePercentTopDown: null,
    oeePercentBottomUp: null,
    teepPercent: 0,
    availabilityPercent: null,
    uptimePercent: null,
    performancePercent: null,
    plannedEfficiencyPercent: null,
    ticksPort2: 0,
  } as DataDumpAggregateReportsType;
};
export enum DaysOfTheWeekEnum {
  Sunday = 0,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export const pnlCategories = [
  PnlCategoriesEnum.waiting,
  PnlCategoriesEnum.unplanned,
  PnlCategoriesEnum.unknown,
  PnlCategoriesEnum.producing,
  PnlCategoriesEnum.notScheduled,
  PnlCategoriesEnum.planned,
];

export type DateTimeContext = {
  timeZone: string;
  year: number;
  weekNumbner: number;
  dayStartHour: number;
  dayStartMinute: number;
  startDate: Date | string | number;
};

export const getWeekAndDayTimeRanges = ({
  year,
  weekNumber,
  dayStartHour,
  dayStartMinute,
  timeZone,
}: {
  timeZone: string;
  year: number;
  weekNumber: number;
  dayStartHour: number;
  dayStartMinute: number;
}): shiftDayWeekTimeRanges => {
  const fullYearArray = generateNewYearArray(
    DaysOfTheWeekEnum.Monday,
    year,
    timeZone
  );

  // start time is given in hours in the desired timezone, to convert back to ours we need to use converToLocalTime
  const zoneDayStartHour = convertToLocalTime(
    new Date().setHours(dayStartHour),
    { timeZone }
  ).getHours();

  const geWeek = setMinutes(
    setHours(new Date(fullYearArray[weekNumber - 1].ge), zoneDayStartHour),
    dayStartMinute
  );

  // Week to date is built to run starting on Monday
  // If date conversions from different timezones causes us to start on the wrong day,
  // Fix the start date here
  let isStartOnMonday = convertToTimeZone(geWeek, { timeZone }).getDay() === 1;
  while (!isStartOnMonday) {
    geWeek.setDate(geWeek.getDate() - 1);
    isStartOnMonday = convertToTimeZone(geWeek, { timeZone }).getDay() === 1;
  }
  const ltWeek = addWeeks(geWeek, 1);

  const geDay1 = geWeek;
  const geDay2 = addDays(geDay1, 1);
  const geDay3 = addDays(geDay2, 1);
  const geDay4 = addDays(geDay3, 1);
  const geDay5 = addDays(geDay4, 1);
  const geDay6 = addDays(geDay5, 1);
  const geDay7 = addDays(geDay6, 1);

  const days = [
    { ge: geDay1, lt: geDay2 },
    { ge: geDay2, lt: geDay3 },
    { ge: geDay3, lt: geDay4 },
    { ge: geDay4, lt: geDay5 },
    { ge: geDay5, lt: geDay6 },
    { ge: geDay6, lt: geDay7 },
    { ge: geDay7, lt: ltWeek },
  ];

  return {
    shifts: [] as SimplifiedShiftSegment[][],
    days: days.map(({ ge, lt }) => ({ ge: ge.valueOf(), lt: lt.valueOf() })),
    week: { ge: geWeek.valueOf(), lt: ltWeek.valueOf() },
  };
};

/** Constructs a 2 dimensional array with an input of an Array, and depth
 *
 * Used primarily to format data for usage through multiple processing loops
 *
 * Based on a 1d array ->  [ "MorningMonday", "NoonMonday" , "NightMonday" , "MorningTuesday", "NoonTuesday", "NightTuesday" ]
 * and depth of -> 3
 *
 * We construct our output: [["MorningMonday", "NoonMonday" , "NightMonday" ],
 *               ["MorningTuesday", "NoonTuesday", "NightTuesday"]
 *              ]
 * @var Arr
 * @var depth -> the dimension
 */
export const unflat = (starting1DArray: any[], depth: number) => {
  // depth of 1 should mean each index should be its own array
  if (depth === 1) {
    return starting1DArray.map(value => [value]);
  }

  const newArr = [] as any[];
  for (let i = 0; i < starting1DArray?.length; i += depth) {
    newArr.push(starting1DArray?.slice(i, i + depth));
  }
  return newArr;
};

// hehehe
export type KindaDataDumpType = {
  [metricName: string]: number;
};
export const sumDataDumpAggregates = (
  dumps: DataDumpAggregateReportsType[] | KindaDataDumpType[]
): KindaDataDumpType => {
  if (dumps.length < 1) {
    return {};
  }
  const summableKeys = [
    "calendarTime",
    "collectableTime",
    "collectedTime",
    "uncollectedTime",
    "producingTime",
    "productionTime",
    "downTime",
    "plannedTime",
    "unplannedTime",
    "waitingTime",
    "unknownTime",
    "runningTime",
    "notScheduledTime",
    "speedLossTime",
    "speedGainTime",
    "rejectLossTime",
    "rejectLossTimeNonProducing",
    "effectiveRunningTime",
    "effectiveProducingTime",
    "effectiveProducingTimeBottomUp",
    "effectiveProducingTimeTopDown",
    "netOutput",
    "grossOutput",
    "rejectOutput",
    "grossOutputNonProducing",
    "collectedPotentialOutput",
    "productionPotentialOutput",
    "producingPotentialOutput",
    "totalPotentialOutput",
    "minorStopTime",
    "ticksPort2",
  ];
  const summed = summableKeys.reduce(
    (agg, k) => ({ ...agg, [k]: 0 }),
    {} as any
  );
  for (const dump of dumps) {
    for (const k of summableKeys) {
      if (summed[k] || summed[k] === 0) {
        summed[k] += dump[k] || dump[k] === 0 ? dump[k] : 0;
      }
    }
  }

  return {
    ...summed,
    availabilityPercent: summed.productionTime
      ? (100 * summed.producingTime) / summed.productionTime
      : null,
    oeePercent: summed.productionTime
      ? (100 * summed.effectiveProducingTimeTopDown) / summed.productionTime
      : null,
    performancePercent: summed.producingTime
      ? (100 * summed.effectiveProducingTimeTopDown) / summed.producingTime
      : null,
  };
};

export const sumDataDumpAggregatesExperimental = (
  dumps: DataDumpAggregateReportsType[]
): DataDumpAggregateReportsType => {
  const summableKeys = [
    "calendarTime",
    "collectableTime",
    "collectedTime",
    "uncollectedTime",
    "producingTime",
    "productionTime",
    "downTime",
    "plannedTime",
    "unplannedTime",
    "waitingTime",
    "unknownTime",
    "runningTime",
    "notScheduledTime",
    "speedLossTime",
    "speedGainTime",
    "rejectLossTime",
    "rejectLossTimeNonProducing",
    "effectiveRunningTime",
    "effectiveProducingTime",
    "effectiveProducingTimeBottomUp",
    "effectiveProducingTimeTopDown",
    "netOutput",
    "grossOutput",
    "rejectOutput",
    "grossOutputNonProducing",
    "collectedPotentialOutput",
    "productionPotentialOutput",
    "producingPotentialOutput",
    "minorStopTime",
    "ticksPort2",
    "totalPotentialOutput",
  ];

  const summedDataDumpAggregate = dumps?.reduce(
    (summedDataDumpAgg, dump: DataDumpAggregateReportsType) => {
      Object.entries(dump).forEach(([key, value]) => {
        if (summableKeys.includes(key)) {
          summedDataDumpAgg[key] += value;
        }
      });

      return summedDataDumpAgg;
    },
    generateEmptyAggregateData() // has to be a function or else successive calls to this constant will cummulate data
  );

  return {
    ...summedDataDumpAggregate,
    availabilityPercent: summedDataDumpAggregate.productionTime
      ? (100 * summedDataDumpAggregate.producingTime) /
        summedDataDumpAggregate.productionTime
      : null,
    oeePercent: summedDataDumpAggregate.productionTime
      ? (100 * summedDataDumpAggregate.effectiveProducingTime) /
        summedDataDumpAggregate.productionTime
      : null,
    performancePercent: summedDataDumpAggregate.producingTime
      ? (100 * summedDataDumpAggregate.effectiveProducingTimeTopDown) /
        summedDataDumpAggregate.producingTime
      : null,
  };
};

export const getShiftNumbers = (dataByShift, uniqueShiftName) => {
  const organizedShiftData = dataByShift.reduce(
    (accumulator, entry, index) => {
      accumulator[index % uniqueShiftName.length].push(entry);
      return accumulator;
    },
    uniqueShiftName.map(() => {
      return [];
    })
  );

  const shiftOutputs = organizedShiftData.map(shiftData => {
    return shiftData.map(({ netOutput }) => {
      return netOutput;
    });
  });

  const productionPotentials = organizedShiftData.map(shiftData => {
    return shiftData.map(({ productionPotentialOutput }) => {
      return productionPotentialOutput;
    });
  });

  const weeklyOutputs = shiftOutputs.map(element => {
    return element.reduce((agg, x) => {
      return agg + x;
    }, 0);
  });

  const weeklyProductionPotentials = productionPotentials.map(
    shiftProductionPotential => {
      return shiftProductionPotential.reduce((agg, x) => {
        return agg + x;
      }, 0);
    }
  );

  const shiftOees = organizedShiftData.map(shiftData => {
    return shiftData.map(({ oeePercent, oeePercentTopDown }) => {
      return { oeePercent, oeePercentTopDown };
    });
  });

  const weeklyOees = organizedShiftData.map((shiftData, index) => {
    return weeklyProductionPotentials[index]
      ? (
          (100 * weeklyOutputs[index]) /
          weeklyProductionPotentials[index]
        ).toFixed(1)
      : "-";
  });

  return {
    shiftOutputs,
    shiftOees,
    weeklyOees,
    weeklyOutputs,
  };
};

export const useLast7Days = ({
  startDate,
  dayStartHour,
  dayStartMinute,
  timeZone,
}) => {
  const initialDate = zonedTimeToUtc(startDate, timeZone);
  const ltWeek = setSeconds(
    setMinutes(setHours(new Date(initialDate), dayStartHour), dayStartMinute),
    0
  );
  const geWeek = subWeeks(ltWeek, 1);

  return [...new Array(NUM_DAYS_IN_WEEK)].map((_, idx) => {
    const ge = addDays(geWeek, idx); // start of week + idx * days
    const lt = addDays(geWeek, idx + 1); // start of week + (idx+1) * days
    return { ge: ge.valueOf(), lt: lt.valueOf() };
  });
};

export type lastXDaysEndTimeType = {
  shifts: any[];
  days: { ge: number; lt: number }[];
  week: { ge: number; lt: number };
};
export const getLastXDaysEndTime = ({
  year,
  weekNumber,
  dayStartHour,
  dayStartMinute,
  dayEndHour,
  dayEndMinute,
  timeZone,
  numDaysInWeek,
}): lastXDaysEndTimeType => {
  const initialDate = zonedTimeToUtc(`${year}-01-01 00:00.0`, timeZone);
  const geWeek = setMinutes(
    setHours(setISODay(setISOWeek(initialDate, weekNumber), 1), dayStartHour),
    dayStartMinute
  );

  // TODO make this usable if day end minutes is diff
  const timeRange = dayEndHour - dayStartHour;

  const days = [...new Array(numDaysInWeek)].map((_, idx) => {
    const ge = addDays(geWeek, idx); // start of week + idx * days
    const lt = addHours(ge, timeRange); // start of week + (idx+1) * days
    return { ge: ge.valueOf(), lt: lt.valueOf() };
  });

  return {
    shifts: [] as any[],
    days,
    week: { ge: geWeek.valueOf(), lt: days[numDaysInWeek - 1].lt.valueOf() },
  };
};

export const getLastXDays24Hours = ({
  year,
  weekNumber,
  dayStartHour,
  dayStartMinute,
  timeZone,
  numDaysInWeek,
}): lastXDaysEndTimeType => {
  const initialDate = zonedTimeToUtc(`${year}-01-01 00:00.0`, timeZone);
  const geWeek = setMinutes(
    setHours(setISODay(setISOWeek(initialDate, weekNumber), 1), dayStartHour),
    dayStartMinute
  );

  const days = [...new Array(numDaysInWeek)].map((_, idx) => {
    const ge = addDays(geWeek, idx); // start of week + idx * days
    const lt = addHours(ge, 24); // start of week + (idx+1) * days
    return { ge: ge.valueOf(), lt: lt.valueOf() };
  });

  return {
    shifts: [] as any[],
    days,
    week: { ge: geWeek.valueOf(), lt: days[numDaysInWeek - 1].lt.valueOf() },
  };
};

/*
 *
 * Algorithm is to find nearest specified day of the week, it's year
 * is the year of the week number. Then get weeks
 * between that date and the first day of that year.
 *
 * Note that dates in one year can be weeks of previous
 * or next year, overlap is up to 3 days.
 *
 * e.g. 2014/12/29 is Monday in week  1 of 2015
 *      2012/1/1   is Sunday in week 52 of 2011
 */
export const getYearAndWeekNumber = ({
  d,
  timeZone,
  dayOfWeek,
}: {
  d: Date;
  timeZone: string;
  dayOfWeek: number | null;
}) => {
  const currentYear = d.getUTCFullYear();
  const oneJan = new Date(currentYear, 0, 1);
  const weekNo =
    oneJan.getDay() == 1 ? getISOWeek(d.getTime()) : getWeek(d.getTime());

  // Return array of year and week number
  return [currentYear, weekNo];
};

/**
 * Gets number of weeks in a given year (52 or 53)
 *
 * @param {*} year
 * @param {*} timeZone
 * @param {*} dayOfWeek
 * @return {*}
 */
export function weeksInYear({
  year,
  timeZone,
}: {
  year: number;
  timeZone: string;
}) {
  const month = 11;
  let day = 31;
  let week;

  // Find week that 31 Dec is in. If is first week, reduce date until
  // get previous week.
  do {
    const d = new Date(year, month, day--);
    [, week] = getYearAndWeekNumber({ d, timeZone, dayOfWeek: 1 });
  } while (week === 1);

  return week;
}

/**
 * Determine if two dates are on the same year, month and day
 *
 * @export
 * @param {{ date1: Date; date2: Date }} { date1, date2 }
 * @return {*}
 */
export function isSameDate({ date1, date2 }: { date1: Date; date2: Date }) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * This method summates the total time of our data dumps into pnlCategories
 *
 * @param data Data Dump data that time will be acquired from
 */
export const getPnlCategoryTimes = (data: any[]): number[] => {
  return pnlCategories.map(tagName => {
    return data.reduce((agg, dataPoint) => {
      if (dataPoint.pnlCategory ? dataPoint.pnlCategory === tagName : false) {
        return agg + (dataPoint.lt - dataPoint.ge) / ONE_HOUR_OF_MS;
      } else {
        return agg;
      }
    }, 0);
  });
};

type DateRange = { ge: Date; lt: Date };
/**
 * This method will generate an array with the GE and LT of all the weeks in a given year
 *
 * @param dayOfTheWeek The day of the week we want to start our weeks
 * @param year The year we want to generate a week array for
 */
export const generateNewYearArray = (
  dayOfTheWeek: DaysOfTheWeekEnum, // e.g. monday, tuesday
  year: number | string,
  timeZone: string // currently implemented as a number, needs string implementation later
): DateRange[] => {
  const firstWeek = [...new Array(NUM_DAYS_IN_WEEK)].map((_, index) => {
    return convertToTimeZone(`${year}-01-0${index + 1}`, { timeZone });
  });

  const firstDay = firstWeek.filter(date => date.getDay() === dayOfTheWeek)[0];

  const dateArray: DateRange[] = [];
  for (let i = 0; i < WEEKS_IN_YEAR; i++) {
    //  we go through every week in the year, and create the upper and lower bounds of time
    // we will then use these upper and lower bounds to query for data
    const ge1 = new Date(firstDay.getTime() + ONE_WEEK_OF_MS * i);
    const lt1 = new Date(firstDay.getTime() + ONE_WEEK_OF_MS * (i + 1));

    if (ge1.getTimezoneOffset() !== firstWeek[0].getTimezoneOffset()) {
      // if the time zone difference does not match, then we need to compensate for daylight savings
      const geDaylightSavings = new Date(ge1.getTime() - ONE_HOUR_OF_MS);
      const ltDaylightSavings = new Date(lt1.getTime() - ONE_HOUR_OF_MS);
      dateArray.push({ ge: geDaylightSavings, lt: ltDaylightSavings });
    } else {
      dateArray.push({ ge: ge1, lt: lt1 });
    }
  } // construct the date in years

  dateArray.unshift({ ge: firstWeek[0], lt: firstDay }); // we add the remaining initial data (that didn't start on the right day of the week)
  // to the start of the array,

  // e.g. jan 1 is on wed, but our weeks start on friday, so the first week of the year should be jan1, jan2

  return dateArray;
};

export const getTagTimeFromAggregateData = (
  dataDumpAggregate: DataDumpAggregateReportsType
): pnlCategoriesData[] => {
  const pnlLosses = dataDumpAggregate.pnlLosses;

  // find unknown time first
  const unknownTimeData = {
    category: PnlCategoriesEnum.unknown,
    tag: "untagged",
    totalTime: round(pnlLosses.unknown.downtimeLossMs / ONE_HOUR_OF_MS, 1),
    timeAndOccurences: `${round(
      pnlLosses.unknown.downtimeLossMs / ONE_HOUR_OF_MS,
      1
    )}, ${pnlLosses.unknown.numberOfEvents}x`,
  };

  const plannedTimeData = pnlLosses.planned.tags.map(
    iterateThroughAggregatePnlCategoryTags(PnlCategoriesEnum.planned)
  );

  const waitingTimeData = pnlLosses.waiting.tags.map(
    iterateThroughAggregatePnlCategoryTags(PnlCategoriesEnum.waiting)
  );

  const unplannedTimeData = pnlLosses.unplanned.tags.map(
    iterateThroughAggregatePnlCategoryTags(PnlCategoriesEnum.unplanned)
  );

  return [
    unknownTimeData,
    ...plannedTimeData,
    ...waitingTimeData,
    ...unplannedTimeData,
  ];
};

export const iterateThroughAggregatePnlCategoryTags =
  (pnlCategory: PnlCategoriesEnum) =>
  (pnlTagLosses: PnlTagLoss): pnlCategoriesData => {
    return {
      category: pnlCategory,
      tag: pnlTagLosses.name,
      totalTime: round(pnlTagLosses.downtimeLossMs / ONE_HOUR_OF_MS, 2),
      timeAndOccurences: `${round(
        pnlTagLosses.downtimeLossMs / ONE_HOUR_OF_MS,
        1
      )}, ${pnlTagLosses.numberOfEvents}x`,
    };
  };

export const getMidgardDataDumps = ({
  tenant,
  machemetryIds,
  ranges,
  isAggregate = false,
}: {
  tenant: string;
  machemetryIds: string[];
  ranges: TimeRangeFinite[];
  isAggregate?: boolean;
}) => {
  return ranges.map(({ ge, lt }) => {
    const { data, loading, error, refetch } = useQuery(
      isAggregate ? DATA_DUMP_AGGREGATE_QUERY : DATA_DUMP_QUERY,
      {
        skip: false,
        client: midgardClients.get(tenant),
        variables: {
          ge: ge.toString(),
          lt: lt.toString(),
          cKey: tenant,
          machemetryIds,
          shouldSkipQuery: false,
        },
        onError: err => {
          console.log(
            "Midgard Data Dump Query Failed, TRYING AGAIN IN 10 SECONDS ",
            err
          );
          setTimeout(refetch, 10 * ONE_SECOND_OF_MS);
        },
      }
    );
    return { data, loading, error };
  });
};
